'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { ShieldAlert, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAds } from './AdContext';
import { useTranslations } from 'next-intl';

const DISMISS_WAIT_SECONDS = 10;

/**
 * Detects ad blockers using three independent signals:
 *   1. DOM bait — a <div> with common ad class names hidden by cosmetic filters.
 *   2. Script bait — a <script> pointing to a known ad URL; uBlock fires onerror.
 *   3. Fetch bait — a HEAD request to the AdSense CDN; blocked requests throw.
 * Returns true if any signal triggers.
 */
async function detectAdBlocker(): Promise<boolean> {
  // 1. DOM bait: uBlock Origin hides elements matching ##.adsbox via injected CSS.
  const domResult = await new Promise<boolean>((resolve) => {
    try {
      const bait = document.createElement('div');
      bait.className = 'adsbox ad-banner ads ad pub_300x250 pub_300x250m pub_728x90';
      bait.style.cssText =
        'display:block!important;position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;';
      bait.innerHTML = '&nbsp;';
      document.body.appendChild(bait);
      // Give uBlock's MutationObserver and CSS injection time to apply.
      setTimeout(() => {
        try {
          const s = window.getComputedStyle(bait);
          const rect = bait.getBoundingClientRect();
          const hidden =
            s.display === 'none' ||
            s.visibility === 'hidden' ||
            s.opacity === '0' ||
            rect.height === 0 ||
            bait.offsetHeight === 0;
          bait.remove();
          resolve(hidden);
        } catch {
          bait.remove();
          resolve(false);
        }
      }, 200);
    } catch {
      resolve(false);
    }
  });
  if (domResult) return true;

  // 2. Script bait: uBlock blocks script loads from known ad domains; onerror fires fast.
  const scriptResult = await new Promise<boolean>((resolve) => {
    try {
      const script = document.createElement('script');
      // Unique query param prevents browser caching from masking a previous block.
      script.src =
        'https://pagead2.googlesyndication.com/pagead/osd.js?_=' + Date.now();
      let done = false;
      const finish = (blocked: boolean) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        script.remove();
        resolve(blocked);
      };
      const timer = setTimeout(() => finish(true), 1500);
      script.onload = () => finish(false);
      script.onerror = () => finish(true);
      document.head.appendChild(script);
    } catch {
      resolve(false);
    }
  });
  if (scriptResult) return true;

  // 3. Fetch bait: blocked requests throw a TypeError in Chromium/Firefox.
  try {
    await fetch(
      'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?_=' + Date.now(),
      { method: 'HEAD', mode: 'no-cors', cache: 'no-store' }
    );
    return false;
  } catch {
    return true;
  }
}

export default function AdBlockerGate({ children }: { children: React.ReactNode }) {
  const t = useTranslations('Auto.AdsAdBlockerGate');
  const { userAllowsAds, routeAllowsAds, user, hydrating } = useAds();
  const isPaid = !!(user?.has_paid_server);
  const pathname = usePathname();

  const [showPopup, setShowPopup] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [countdown, setCountdown] = useState(DISMISS_WAIT_SECONDS);
  const [dismissReady, setDismissReady] = useState(false);

  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastPathRef = useRef<string | null>(null);

  const stopCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const startCountdown = useCallback(() => {
    stopCountdown();
    setCountdown(DISMISS_WAIT_SECONDS);
    setDismissReady(false);
    const start = Date.now();
    countdownRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - start) / 1000);
      const left = DISMISS_WAIT_SECONDS - elapsed;
      if (left <= 0) {
        setCountdown(0);
        setDismissReady(true);
        stopCountdown();
      } else {
        setCountdown(left);
      }
    }, 250);
  }, [stopCountdown]);

  const runDetection = useCallback(async () => {
    // Only run on ad-eligible routes. We check userAllowsAds too so paid users
    // (who have ads disabled) are never shown the popup. `hydrating` is checked
    // separately (not folded into userAllowsAds) because on pages that mount
    // AdWrapper without a server-rendered `user` (e.g. /blog), userAllowsAds
    // defaults to `true` for the brief window before /api/auth/me resolves -
    // without this guard, a paid/premium user with an ad blocker would see the
    // popup flash on screen and then vanish once their plan loads.
    if (!routeAllowsAds || userAllowsAds === false || isPaid || hydrating) {
      setShowPopup(false);
      stopCountdown();
      return;
    }
    const blocked = await detectAdBlocker();
    if (blocked) {
      setShowPopup(true);
      startCountdown();
    } else {
      setShowPopup(false);
      stopCountdown();
    }
  }, [userAllowsAds, routeAllowsAds, isPaid, hydrating, startCountdown, stopCountdown]);

  // Re-run detection on every page navigation
  useEffect(() => {
    lastPathRef.current = pathname;
    runDetection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Re-run when ad eligibility changes (e.g. user logs in/out) or once
  // hydration finishes resolving the user's real plan/ad-flag state.
  useEffect(() => {
    if (lastPathRef.current !== null) runDetection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAllowsAds, routeAllowsAds, hydrating]);

  useEffect(() => {
    return () => stopCountdown();
  }, [stopCountdown]);

  const handleDismiss = () => {
    if (!dismissReady) return;
    setShowPopup(false);
    stopCountdown();
  };

  const handleRecheck = async () => {
    setRechecking(true);
    const blocked = await detectAdBlocker();
    setRechecking(false);
    if (!blocked) {
      setShowPopup(false);
      stopCountdown();
    }
  };

  return (
    <>
      {children}

      <Dialog
        open={showPopup}
        onOpenChange={(next) => {
          // Only allow closing via the buttons — not overlay click or Escape.
          if (!next) return;
        }}
      >
        <DialogContent showCloseButton={false} className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/15 text-destructive">
              <ShieldAlert className="h-6 w-6" />
            </div>
            <DialogTitle className="text-center">{t('adBlockerDetected')}</DialogTitle>
            <DialogDescription className="text-center">
              {t('weRelyOnAds')}
            </DialogDescription>
          </DialogHeader>

          <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
            <li>{t('clickTheAdBlocker')}</li>
            <li>Disable it for <span className="font-mono">apect.net</span>.</li>
            <li>Reload this page or click &ldquo;I&rsquo;ve disabled it&rdquo; below.</li>
          </ol>

          <div className="mt-2 flex flex-col gap-2">
            <Button onClick={handleRecheck} disabled={rechecking} className="w-full">
              {rechecking ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Re-checking…</>
              ) : (
                <><RefreshCw className="mr-2 h-4 w-4" /> I&rsquo;ve disabled it</>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleDismiss}
              disabled={!dismissReady}
              className="w-full"
            >
              {dismissReady ? t('dismiss') : `Dismiss (${countdown}s)`}
            </Button>
            <p className="text-center text-[11px] text-muted-foreground">
              {t('thisNoticeAppearsOn')}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
